import { readOnlyPreview } from "./leagueStore";
import React from "react";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import { currYear, getWeek, isPassedDue } from "./App";
import {
  listChallengesForYear,
  settleChallenge,
  canSettleChallenge,
  publicDecisionLabel,
  ensureChallengeDoc,
  getChallengeForWeek,
} from "./challengeHelpers";
import ChallengeOfferModal from "./ChallengeOfferModal";
import { challengesSchedule2026 } from "./challengesSchedule2026";

class StatsChallenges extends React.Component {
  constructor(props) {
    super(props);
    const currentWeek = getWeek();
    this.state = {
      challenges: [],
      loaded: false,
      error: "", busy: false,
      previewWeek: currentWeek >= 1 && currentWeek <= 17 ? currentWeek : 1,
      showPreviewModal: false,
      previewChallenge: null,
    };
  }

  componentDidMount() {
    this.loadChallenges();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isAdmin !== this.props.isAdmin) {
      this.loadChallenges();
    }
  }

  /** Ensure docs exist for scheduled weeks so Stats has a full season list. */
  seedMissingWeeks = async () => {
    for (let week = 1; week <= 17; week++) {
      if (!getChallengeForWeek(week)) continue;
      if (!isPassedDue(week)) continue;
      await ensureChallengeDoc(week);
    }
  };

  loadChallenges = async () => {
    try {
      await this.seedMissingWeeks();
    } catch (e) {
      console.warn("Challenge seed:", e);
    }
    let all;
    try { all = await listChallengesForYear(currYear); }
    catch { this.setState({ loaded: true, error: "Could not load challenges. Refresh to try again." }); return; }
    const challenges = all.filter((ch) => isPassedDue(ch.Week));
    this.setState({ challenges, loaded: true });
  };

  handleSettle = async (challengeId, outcome) => {
    this.setState({ busy: true, error: "" });
    try { await settleChallenge(challengeId, outcome); await this.loadChallenges(); }
    catch (error) { this.setState({ error: error.message || "Could not save the result. Try again." }); }
    finally { this.setState({ busy: false }); }
  };

  openPreview = () => {
    const week = Number(this.state.previewWeek);
    const challenge = getChallengeForWeek(week);
    if (!challenge) return;
    this.setState({ previewChallenge: challenge, showPreviewModal: true });
  };

  outcomeLabel(ch) {
    const o = (ch.Outcome || "").toLowerCase();
    if (o === "completed") return "Completed";
    if (o === "failed") return "Failed";
    return "—";
  }

  renderPreviewPanel(isAdmin) {
    if (!isAdmin) return null;

    const weeks = challengesSchedule2026.weeks.map((w) => w.week);

    return (
      <Row
        style={{
          marginBottom: "16px",
          padding: "12px",
          background: "#f8f9fa",
          borderRadius: "6px",
          textAlign: "left",
        }}
      >
        <Col>
          <div style={{ fontWeight: "bolder", marginBottom: "8px" }}>
            Preview recipient popup (admin)
          </div>
          <p style={{ fontSize: "0.9rem", marginBottom: "10px" }}>
            See exactly what the weekly challenge recipient sees after they
            submit picks or rankings — no Firestore changes.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
            <Form.Select aria-label="Challenge preview week"
              style={{ maxWidth: "120px" }}
              value={this.state.previewWeek}
              onChange={(e) =>
                this.setState({ previewWeek: Number(e.target.value) })
              }
            >
              {weeks.map((w) => (
                <option key={w} value={w}>
                  Week {w}
                </option>
              ))}
            </Form.Select>
            <Button variant="outline-primary" onClick={this.openPreview}>
              Show challenge popup
            </Button>
          </div>
        </Col>
      </Row>
    );
  }

  render() {
    const week = getWeek();
    const { challenges, loaded } = this.state;
    const isAdmin = this.props.isAdmin === true;

    if (!loaded) {
      return (
        <Container>
          <Row>
            <Col>Loading challenges…</Col>
          </Row>
        </Container>
      );
    }

    return (
      <Container>
        {this.renderPreviewPanel(isAdmin)}
        <div className="stats-section-heading"><h2>Weekly challenges</h2><span className="keeper-season">{currYear} season</span></div>
        <p className="stats-context">The weekly dare, the decision, and how it turned out.</p>
        {this.state.error && <p role="alert" className="inline-notice error">{this.state.error}</p>}
        {!challenges.length && <div className="stats-empty">Challenges appear here after Thursday's 7:30 p.m. CT deadline.</div>}
        <div className="challenge-history">
          {challenges.map(ch => <article className="challenge-history-card" key={ch.id || ch.Week}>
            <header><span className="personal-eyebrow">Week {ch.Week}</span><span className="challenge-reward">${ch.Faab} <small>FAAB</small></span></header>
            <h3>{ch.RecipientName}</h3><p className="challenge-task-copy">{ch.Text}</p>
            <footer><span className={`challenge-status ${ch.Outcome || ch.Decision}`}>{ch.Outcome ? this.outcomeLabel(ch) : publicDecisionLabel(ch.Decision)}</span>
              <span>{ch.Outcome === "completed" ? "Reward earned · Sleeper credit handled separately" : ch.Outcome === "failed" ? "No reward earned" : ch.Decision === "accepted" ? "Awaiting commissioner review" : "No result to review"}</span></footer>
            {canSettleChallenge(ch, isAdmin, week) && <div className="challenge-settlement"><strong>Commissioner review</strong><p>Did {ch.RecipientName} achieve this challenge?</p>
              <div className="admin-actions"><Button disabled={readOnlyPreview || this.state.busy} onClick={() => this.handleSettle(ch.id, "completed")}>Mark achieved</Button><Button variant="outline-secondary" disabled={readOnlyPreview || this.state.busy} onClick={() => this.handleSettle(ch.id, "failed")}>Mark failed</Button></div>
              {readOnlyPreview && <p>Read-only preview: result saving is disabled.</p>}
            </div>}
          </article>)}
        </div>

        <ChallengeOfferModal
          show={this.state.showPreviewModal}
          onHide={() =>
            this.setState({ showPreviewModal: false, previewChallenge: null })
          }
          challenge={this.state.previewChallenge}
          preview
        />
      </Container>
    );
  }
}

export default StatsChallenges;
